import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import * as THREE from "three";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF } from "@react-three/drei";
import { Plant } from "./Display";

type Ref<T> = React.Ref<T>;

export enum Scene {
    Welcome,
    Plants,

    Plant1,
    Plant2,
    Plant3,
}

const scenes = {
    [Scene.Welcome]: () => {
        const welcomeSign = useGLTF("./assets/models/welcome-sign.glb");
        return <primitive object={welcomeSign.scene} position={[-1.5, 0.05, -0.5]} scale={0.1} rotation={[0, 0.1, 0]} />;
    },
    [Scene.Plants]: () => {
        const plantPot = useGLTF("./assets/models/plant-pot.glb");
        const [plantPot1, plantPot2, plantPot3] = [plantPot.scene.clone(), plantPot.scene.clone(), plantPot.scene.clone()];
        const plant1 = useGLTF("./assets/models/plants/common-camas.glb");
        const plant2 = useGLTF("./assets/models/plants/salal.glb");
        const plant3 = useGLTF("./assets/models/plants/western-red-cedar.glb");
        return (
            <>
                <primitive object={plantPot1} position={[-1, 0.05, 0]} scale={0.3} rotation={[0, 0, 0]} />
                <primitive object={plant1.scene} position={[-1, 0.3, 0]} scale={0.4} rotation={[0, 0, 0]} />
                <primitive object={plantPot2} position={[0.0, 0.05, 0]} scale={0.3} rotation={[0, 0, 0]} />
                <primitive object={plant2.scene} position={[0.0, 0.3, 0]} scale={0.15} rotation={[0, 0, 0]} />
                <primitive object={plantPot3} position={[1, 0.05, 0]} scale={0.3} rotation={[0, 0, 0]} />
                <primitive object={plant3.scene} position={[1, 0.3, 0]} scale={0.4} rotation={[0, 0, 0]} />
            </>
        );
    },
    [Scene.Plant1]: () => (<></>),
    [Scene.Plant2]: () => (<></>),
    [Scene.Plant3]: () => (<></>),
};



export default function Background({ scene, plant, index }: { scene: Scene, plant?: Plant, index: number }) {
    const wood = useLoader(TextureLoader, "./assets/wood.png");
    wood.wrapS = THREE.RepeatWrapping;
    wood.wrapT = THREE.RepeatWrapping;
    wood.magFilter = THREE.NearestFilter;

    const [renderScene, setRenderScene] = useState<() => JSX.Element>(() => scenes[scene]);
    const [transitioning, setTransitioning] = useState(false);
    const [shouldShowPlant, setShouldShowPlant] = useState(false);

    useEffect(() => {
        if (scene === Scene.Welcome) return;
        setTransitioning(true);
        setShouldShowPlant(false);
        setTimeout(() => {
            setRenderScene(() => scenes[scene]);
            setShouldShowPlant(true);
        }, 1000);
        setTimeout(() => {
            setTransitioning(false);
        }, 2000);
    }, [scene]);

    const lookingAtSomething = !!plant && index > 0;
    const infoIndex = index - 1;
    return (
        <Canvas
            camera={{ fov: 50 }}
            className={transitioning ? 'sceneTransition' : ''}
        >
            <ambientLight color={[0.2, 0.2, 0.2]} />
            <AnimatedLight />
            <mesh position={[0, -1.5, 0]} rotation={[-Math.PI / 2, 0, 0]} >
                <planeGeometry args={[1000, 1000]} />
                <meshBasicMaterial color="black" />
            </mesh>
            <mesh scale={0.5} rotation={[0, Math.PI / 2, 0]} position={[0, -0.5, 0]}>
                <ObjToPrimitive url="./assets/table.obj" mat={new THREE.MeshPhongMaterial({ map: wood })} />
            </mesh>
            {renderScene()}
            <CameraWobbler 
                lookingAtSomething={lookingAtSomething} 
                target={lookingAtSomething ? plant.infoPoints[infoIndex].position : [0,0,0]}
                targetLookAt={lookingAtSomething ? plant.infoPoints[infoIndex].lookAt : [0,0,0]}
            />
            {plant && shouldShowPlant && plant.infoPoints.map((infoPoint, i) => infoPoint.Object(i))}
        </Canvas>
    ); 
}

export function AnimatedLight() {
    const light = useRef<THREE.PointLight>();
    useFrame(({ clock }) => {
        if (light.current) {
            light.current.position.y = Math.exp(-clock.getElapsedTime() + 3) + 3;
        }
    });
    return (
        <pointLight ref={light as Ref<THREE.PointLight>} position={[0, 100, 2]} intensity={10} color="white" />
    );
}

function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
}

let mouseX = 0;
document.addEventListener("mousemove", (e) => {
    mouseX = e.clientX;
});
let interpX = 0;

let cameraPos: [ number, number, number ] = [ 0, 0.5, 2 ];
let cameraLookAt: [ number, number, number ] = [ 0, 0, 0 ];

export function CameraWobbler({ target, targetLookAt, lookingAtSomething }: { target: [number, number, number], targetLookAt: [number, number, number], lookingAtSomething: boolean }) {
    const camera = useThree((state) => state.camera);

    useFrame(() => {
        const width = window.innerWidth;
        const x = (mouseX / width * 2 - 1) * 0.1;
        interpX = lerp(interpX, x, 0.2);

        let targetX, targetY, targetZ;
        let lookAtX, lookAtY, lookAtZ;
        if (lookingAtSomething) {
            [targetX, targetY, targetZ] = target;
            [lookAtX, lookAtY, lookAtZ] = targetLookAt;
        } else {
            [targetX, targetY, targetZ] = [0, 0.5, 2];
            [lookAtX, lookAtY, lookAtZ] = [interpX, 0.5, -0.5];
        }

        cameraPos = [lerp(cameraPos[0], targetX, 0.1), lerp(cameraPos[1], targetY, 0.1), lerp(cameraPos[2], targetZ, 0.1)];
        cameraLookAt = [lerp(cameraLookAt[0], lookAtX, 0.1), lerp(cameraLookAt[1], lookAtY, 0.1), lerp(cameraLookAt[2], lookAtZ, 0.1)];

        camera.position.set(...cameraPos);
        camera.lookAt(...cameraLookAt);

    });

    return <></>;
}


export function ObjToPrimitive({ url, mat }: { url: string; mat: THREE.Material }) {
    const [obj, setObj] = useState<THREE.Object3D>();
    useMemo(() => new OBJLoader().load(url, setObj), [url]);
    if (obj) {
      obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material = mat;
        }
      });
      return <primitive object={obj} />;
    }
    return null;
  }